// An installed airline app (PWA) belongs to that airline: it opens at /login/<code>?source=pwa,
// and from then on every sign-in page in that window stays on the airline — signing out, an
// expired session or a link to another airline's (or the unbranded) sign-in page all come back
// to /login/<code>. sessionStorage is per window, so a normal browser tab is never pinned.
import { normaliseCode } from './brandStorage'

export const PINNED_BRAND_KEY = 'pinned_brand'

function storage() {
  try {
    return window.sessionStorage
  } catch (e) {
    return null
  }
}

/** Called once at startup: an app launch (/login/<code>?source=pwa) pins the window. */
export function capturePinnedBrand(location = window.location) {
  const params = new URLSearchParams(location.search || '')
  if (params.get('source') !== 'pwa') return null
  const match = /^\/login\/([^/]+)\/?$/.exec(location.pathname || '')
  const code = match && normaliseCode(match[1])
  if (code) storage()?.setItem(PINNED_BRAND_KEY, code)
  return code
}

export function pinnedBrandCode() {
  return normaliseCode(storage()?.getItem(PINNED_BRAND_KEY)) || null
}

// The sign-in pages, without their optional trailing airline code. ':token' is any one segment.
const AUTH_PAGES = [
  ['login'],
  ['forgot-password'],
  ['reset-password', ':token'],
  ['mfa-verify'],
  ['sso-callback'],
  ['sso', 'error'],
  ['sso', 'link', 'complete'],
]

/**
 * Where a sign-in page must go in a window pinned to `pinned`: the same page with the pinned
 * airline's code, or null when it already is (or the path is not a sign-in page).
 */
export function pinnedAuthPath(pathname, pinned) {
  if (!pinned) return null
  const segments = String(pathname || '/')
    .split('/')
    .filter(Boolean)
  if (segments.length === 0) return `/login/${pinned}`
  for (const page of AUTH_PAGES) {
    const head = segments.slice(0, page.length)
    const rest = segments.slice(page.length)
    const matches =
      head.length === page.length && page.every((part, i) => part === ':token' || part === head[i])
    if (matches && rest.length <= 1) {
      if (rest.length === 1 && rest[0].toLowerCase() === pinned) return null
      return `/${head.join('/')}/${pinned}`
    }
  }
  return null
}

/** Does the signed-in user belong to the airline `code`? (Vamos staff/developers: never.) */
export function authBelongsTo(auth, code) {
  return (
    auth?.user?.type === 'company' && normaliseCode(auth.user.company?.code) === normaliseCode(code)
  )
}
